import { Package, RegistrationAffiliation } from "@/api/dtos";
import { EARLY_BIRD } from "@/constants/constants";
import { AdminContext } from "@/contexts/AdminContext";
import PackageContext, { IPackageContext } from "@/contexts/PackageContext";
import useAdditionalSessions from "@/hooks/useAdditionalSessions";
import useAvailablePackages from "@/hooks/useAvailablePackages";
import { useScopedField } from "@ngt/forms-core";
import { DateTime } from "luxon";
import { FC, PropsWithChildren, useContext, useMemo } from "react";


const useFormPackages = () => {
    const {state: {value: member}} = useScopedField<number>("member", {value: true});
    const {state: {value: trialCoordinator}} = useScopedField<boolean>("trialCoordinator", {value: true});
    const {state: {value: student}} = useScopedField<boolean>("student", {value: true});
    const {state: {value: registrar}} = useScopedField<boolean>("registrar", {value: true});
    const {state: {value: staff}} = useScopedField<boolean>("staff", {value: true});
    const {state: {value: attendeeFromJapanOrKorea}} = useScopedField<boolean>("attendeeFromJapanOrKorea", {value: true});
    const {state: {value: isSac}} = useScopedField<boolean>("isSac", {value: true});
    const {state: {value: sponsor}} = useScopedField<boolean>("sponsor", {value: true});
    const {state: {value: freeSponsorRegistration}} = useScopedField<boolean>("freeSponsorRegistration", {value: true});
    
    const {state: { value: registrationAffiliation }} = useScopedField<number>("registrationAffiliation", { value: true });

    const isCap = registrationAffiliation === RegistrationAffiliation.CAP;
    // Treated as speaker if any affiliation is selected
    const isSpeaker = registrationAffiliation != null;

    return useAvailablePackages(member ?? undefined,  
        trialCoordinator ?? undefined, 
        student ?? undefined, 
        registrar ?? undefined, 
        staff ?? undefined, 
        attendeeFromJapanOrKorea ?? undefined, 
        sponsor ?? undefined, 
        freeSponsorRegistration ?? undefined, 
        isSac ?? undefined, 
        isCap ?? undefined, 
        isSpeaker ?? undefined); // , online ?? undefined
}

const useEarlyBird = () => {
    const {state: {value: enteredDate}} = useScopedField<string>("enteredDate", {value: true}, undefined, false);

    return useMemo(() => {
        let comparisonDate = new Date();

        if (enteredDate) {
            comparisonDate = DateTime.fromISO(enteredDate).toJSDate();
        }

        return EARLY_BIRD >= comparisonDate;
    }, [enteredDate]);
}

const usePackage = (packages?: Package[]): Package | undefined => {
    const {state: { value: packageId }} = useScopedField<number>("packageId", { value: true }, undefined, false);

    return useMemo(() => {
        if (!packageId || !packages) {
            return undefined;
        }

        return packages.find(x => x.id === packageId);
    }, [packageId, packages])
}

const PackageProvider: FC<PropsWithChildren> = ({
    children
}) => {
    const isAdmin = useContext(AdminContext);
    const {state: { value: registrationAffiliation }} = useScopedField<number>("registrationAffiliation", { value: true }, undefined, false);
    const {state: { value: isTAndECSpeaker }} = useScopedField<boolean>("isTAndECSpeaker", { value: true }, undefined, false);
    
    const hasSpeakerPrivileges = useMemo(() => {
        return registrationAffiliation != null;
    }, [registrationAffiliation]);

    const { packages, loading } = useFormPackages();
    const packageInformation = usePackage(packages);
    const isEarlyBird = useEarlyBird();
    const { additionalSessions, loading: additionalSessionsLoading } = useAdditionalSessions(packageInformation?.id, isAdmin, hasSpeakerPrivileges ?? false, isTAndECSpeaker ?? false);

    const context = useMemo(() => {
        const c: IPackageContext = {
            packages: packages,
            packagesLoading: loading,
            package: packageInformation,
            isEarlyBird: isEarlyBird,
            additionalSessions: additionalSessions,
            additionalSessionsLoading: additionalSessionsLoading
        }

        return c;
    }, [packages, loading, packageInformation, isEarlyBird, additionalSessions, additionalSessionsLoading]);

    return (
        <PackageContext.Provider value={context}>
            {children}
        </PackageContext.Provider>
    )
}

export default PackageProvider;